import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { PenTool, BookOpen, Users, FileText } from 'lucide-react';
import { AuthProvider } from '@/contexts/AuthContext';
import LoginPage from '@/pages/LoginPage';
import SignUpPage from '@/pages/SignUpPage';
import DashboardPage from '@/pages/DashboardPage';
import CreateUnitPage from '@/pages/CreateUnitPage';

function HomePage() {
  const features = [
    {
      icon: <PenTool className="w-6 h-6" />,
      title: 'Minute Keeping',
      description: 'Maintain detailed minutes of unit proceedings with elegance and precision.'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Summons Generation',
      description: 'Create and distribute official summons to members efficiently.'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Membership',
      description: 'Track and manage unit membership records with meticulous care.'
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Communication',
      description: 'Handle official communications and correspondence with members.'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 border border-black rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 border border-black rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="container mx-auto px-6 py-32 relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Ornamental Divider */}
            <div className="flex items-center justify-center mb-8">
              <div className="h-px w-16 bg-black"></div>
              <div className="mx-4 w-2 h-2 bg-black rotate-45"></div>
              <div className="h-px w-16 bg-black"></div>
            </div>

            <h1 className="font-cursive text-8xl md:text-9xl lg:text-[12rem] font-normal mb-8 tracking-wide">
              Secretary
            </h1>
            
            <a 
              href="https://masonic-tools.co.uk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-heading text-sm md:text-base mb-4 text-gray-600 font-light tracking-[0.3em] uppercase hover:text-black transition-colors duration-300 inline-block"
            >
              Part of Masonic Tools
            </a>
            
            <p className="font-body text-lg md:text-xl max-w-2xl mx-auto text-gray-600 leading-relaxed mb-12 font-light">
              A distinguished platform for unit secretaries to maintain the sacred trust
              of record-keeping with precision, elegance, and reverence for tradition.
            </p>

            {/* Ornamental Divider */}
            <div className="flex items-center justify-center mb-16">
              <div className="h-px w-16 bg-black"></div>
              <div className="mx-4 w-2 h-2 bg-black rotate-45"></div>
              <div className="h-px w-16 bg-black"></div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-12 py-5 bg-black text-white font-heading text-base tracking-[0.3em] uppercase transition-all duration-300 cursor-default">
                Coming Soon
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-black to-transparent"></div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-5xl md:text-6xl font-bold mb-4 tracking-tight">
              Essential Functions
            </h2>
            <p className="font-body text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Tools crafted with care for the modern unit secretary
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative p-8 bg-white border border-gray-200 transition-all duration-300 hover:border-black hover:shadow-2xl"
              >
                {/* Corner Decorations */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-black opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-black opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-black opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-black opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                <div className="mb-4 text-black transition-transform duration-300 group-hover:scale-110">
                  {feature.icon}
                </div>
                
                <h3 className="font-heading text-2xl font-semibold mb-3 text-black tracking-tight">
                  {feature.title}
                </h3>
                
                <p className="font-body text-gray-600 leading-relaxed font-light">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <span className="font-display text-6xl opacity-50">"</span>
            </div>
            
            <blockquote className="font-heading text-3xl md:text-4xl font-light mb-8 leading-relaxed tracking-wide">
              In the careful keeping of records lies the preservation of our sacred traditions
              and the continuity of our ancient craft.
            </blockquote>
            
            <div className="flex items-center justify-center">
              <div className="h-px w-16 bg-white"></div>
              <div className="mx-4 w-2 h-2 bg-white rotate-45"></div>
              <div className="h-px w-16 bg-white"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-200">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="font-body text-sm text-gray-600 tracking-wide">
              Part of the Masonic Tools Suite
            </p>
            <p className="font-body text-xs text-gray-500 mt-2">
              © {new Date().getFullYear()} All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/unit/create" element={<CreateUnitPage />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;

